import {
  isPurchase,
  isSubstituted,
  outcomeOf,
  priceEvidence,
} from './shoppingTripOutcome';

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const UNMAPPED_PL_CATEGORY = 'Unmapped';
export const UNCATEGORIZED_SHOPPING = 'Uncategorized';

export function formatShoppingDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
/** The amount currently represented by a purchased line, updated as actuals change. */
export function purchasedLineTotal(item) {
  if (!item || !isPurchase(item)) return null;
  const evidence = priceEvidence(item);
  if (evidence.lineTotal !== null) return evidence.lineTotal;
  const quantity = toFiniteNumber(item.actual_quantity) ?? toFiniteNumber(item.packages);
  const price = toFiniteNumber(item.actual_price) ?? toFiniteNumber(item.estimated_price);
  if (quantity !== null && price !== null) return quantity * price;
  return toFiniteNumber(item.estimated_line_total);
}

export function shoppingListStats(items) {
  const rows = (Array.isArray(items) ? items : []).filter(Boolean);
  const outcomes = rows.map((item) => outcomeOf(item));
  return {
    total: rows.length,
    purchased: outcomes.filter((outcome) => outcome === 'found').length,
    substituted: rows.filter(isSubstituted).length,
    notPurchased: outcomes.filter((outcome) => outcome === 'not_purchased').length,
    pending: outcomes.filter((outcome) => outcome === 'pending').length,
  };
}

const itemName = (item) => String(item?.description || 'Item');
const alphaItems = (items) => [...items].sort((a, b) => itemName(a).localeCompare(itemName(b)));

/**
 * Category is the store-walk default. The other modes keep every line visible
 * and bring the requested outcome to the top, rather than acting as filters.
 */
export function groupShoppingListItems(items, mode = 'category') {
  const rows = (Array.isArray(items) ? items : []).filter(Boolean);
  if (mode === 'category') {
    const groups = new Map();
    rows.forEach((item) => {
      const category = String(item.category_snapshot || '').trim() || UNCATEGORIZED_SHOPPING;
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(item);
    });
    return [...groups.entries()]
      .sort(([a], [b]) => {
        if ((a === UNCATEGORIZED_SHOPPING) !== (b === UNCATEGORIZED_SHOPPING)) {
          return a === UNCATEGORIZED_SHOPPING ? 1 : -1;
        }
        return a.localeCompare(b);
      })
      .map(([label, groupItems]) => ({ label, items: alphaItems(groupItems) }));
  }

  const definitions = mode === 'purchased'
    ? [
      ['Purchased', (item) => outcomeOf(item) === 'found'],
      ['Other items', (item) => outcomeOf(item) !== 'found'],
    ]
    : mode === 'not-purchased'
      ? [
        ['Not purchased', (item) => outcomeOf(item) === 'not_purchased'],
        ['Other items', (item) => outcomeOf(item) !== 'not_purchased'],
      ]
      : [
        ['Substituted', isSubstituted],
        ['Other items', (item) => !isSubstituted(item)],
      ];

  return definitions
    .map(([label, predicate]) => ({ label, items: alphaItems(rows.filter(predicate)) }))
    .filter((group) => group.items.length > 0);
}

export function buildShoppingFinancials(items, {
  taxAmount = null,
  otherCharges = null,
  pnlCategoryByInventoryCategory = {},
  hasPnlMappings = false,
} = {}) {
  const rows = (Array.isArray(items) ? items : []).filter(Boolean);
  let purchaseTotal = 0;
  const pnl = new Map();

  rows.forEach((item) => {
    const amount = purchasedLineTotal(item);
    if (amount === null) return;
    purchaseTotal += amount;
    if (!hasPnlMappings) return;
    const inventoryCategory = String(item.category_snapshot || '').trim().toLowerCase();
    const pnlName = pnlCategoryByInventoryCategory[inventoryCategory] || UNMAPPED_PL_CATEGORY;
    pnl.set(pnlName, (pnl.get(pnlName) || 0) + amount);
  });

  const tax = Math.max(0, toFiniteNumber(taxAmount) ?? 0);
  const other = Math.max(0, toFiniteNumber(otherCharges) ?? 0);
  return {
    purchaseTotal,
    tax,
    otherCharges: other,
    total: purchaseTotal + tax + other,
    pnlTotals: [...pnl.entries()]
      .map(([label, amount]) => ({ label, amount }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  };
}
